import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import DataTable, { useServerTable } from '../../components/ui/DataTable';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Spinner from '../../components/ui/Spinner';
import { adminApi } from '../../config/axios';
import { connectivityBadge } from '../../utils/connectivityBadge';
import { useGlobal } from '../../contexts/GlobalContext';
import { dateFormat, useDocumentTitle } from '../../config/utils';
import { useForm } from 'react-hook-form';

export default function DevicesList() {
  useDocumentTitle('Devices');
  const navigate       = useNavigate();
  const { showToast }  = useGlobal();
  const [search, setSearch]         = useState('');
  const [editDevice, setEditDevice] = useState(null);
  const [deleteDevice, setDeleteDevice] = useState(null);
  const [deleting, setDeleting]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [selected, setSelected]     = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetcher = useCallback(async ({ page, perPage, sortField, sortOrder }) => {
    const res = await adminApi.post(`/datatable/device/view?page=${page + 1}&per_page=${perPage}`, {
      order_by: { [sortField]: sortOrder },
      filters: {
        name:        { value: search, matchMode: 'contains' },
        serial_name: { value: search, matchMode: 'contains' },
      },
    });
    return res.data;
  }, [search]);

  const table = useServerTable(fetcher);

  // Keep the Online badge in sync with the tenant Screens page, which polls
  // every 15s (backend marks a device offline after ONLINE_THRESHOLD_SECONDS).
  // This page previously fetched once per page/sort/filter change and never
  // refreshed, so it could disagree with the tenant dashboard for the same device.
  useEffect(() => {
    const interval = setInterval(() => table.refresh(), 15000);
    return () => clearInterval(interval);
  }, [table.refresh]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const openEdit = (d) => {
    setEditDevice(d);
    reset({ name: d.name });   // serial is displayed read-only, not a form field
  };

  const onSave = async (data) => {
    setSaving(true);
    try {
      // PLURAL /devices/{id}/update — the singular `/device/{id}` this used to
      // call has no PUT route at all (that group is GET/POST/DELETE only), so
      // every save failed. Only `name` is sent: serial_name is the device's
      // identity and its RTDB node key, and the endpoint rejects it.
      await adminApi.put(`/devices/${editDevice.id}/update`, { name: data.name });
      showToast('success', 'Updated', 'Device updated.');
      setEditDevice(null);
      table.refresh();
    } catch (err) {
      showToast('error', 'Error', err?.response?.data?.message ?? 'Failed.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await adminApi.delete(`/device/${deleteDevice.id}`);
      showToast('success', 'Deleted', 'Device removed.');
      setDeleteDevice(null);
      table.refresh();
    } catch {
      showToast('error', 'Error', 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const bulkAction = async (command) => {
    if (!selected.length) return;
    setBulkLoading(true);
    try {
      await Promise.all(selected.map(id => adminApi.post(`/device/${id}/command`, { command })));
      showToast('success', 'Bulk Action', `${command} sent to ${selected.length} devices.`);
      setSelected([]);
    } catch {
      showToast('error', 'Error', 'Some commands failed.');
    } finally {
      setBulkLoading(false);
    }
  };

  const columns = [
    { key: 'serial_name', header: 'Serial', render: r => <code className="text-xs font-mono text-text-muted">{r.serial_name}</code> },
    { key: 'name',        header: 'Name' },
    { key: 'company_name', header: 'Company' },
    // The branch supplies the device's scheduling timezone when it has one
    // (branch -> company -> Asia/Riyadh), so it belongs next to the company.
    { key: 'branch',      header: 'Branch', render: r => (
      r.branch?.name ?? <span className="text-text-muted">—</span>
    )},
    {
      // 2026-10-07 presence-states follow-up — the datatable row already
      // carries `connectivity` (Device::$appends, never queried/filtered
      // by this column, just rendered), so standby/app_closed finally show
      // as their own state instead of collapsing into "Offline" the way
      // the plain is_online boolean did.
      key: 'is_online', header: 'Status', render: r => {
        const { variant, label } = connectivityBadge(r.connectivity?.state);
        return <Badge variant={variant} dot>{label}</Badge>;
      },
    },
    { key: 'rotation',    header: 'Rotation', render: r => `${r.rotation ?? 0}°` },
    { key: 'status',      header: 'Status', render: r => (
      <Badge variant={r.status === 'ACTIVE' ? 'teal' : r.status === 'EXPIRED' ? 'danger' : 'gold'}>
        {r.status?.toLowerCase()}
      </Badge>
    )},
    { key: 'created_at',  header: 'Added', render: r => dateFormat(r.created_at) },
  ];

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h1 className="section-title text-xl">Devices</h1>
          <p className="text-text-muted text-sm mt-0.5">{table.total} total devices</p>
        </div>
        {selected.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-muted">{selected.length} selected</span>
            <button disabled={bulkLoading} onClick={() => bulkAction('restart')} className="btn-secondary py-1.5 text-xs">
              {bulkLoading ? <Spinner size="sm" /> : '↺ Restart All'}
            </button>
            <button disabled={bulkLoading} onClick={() => bulkAction('mute')} className="btn-secondary py-1.5 text-xs">
              🔇 Mute All
            </button>
          </div>
        )}
      </div>

      <div className="mb-5">
        <Input
          placeholder="Search device name or serial…"
          value={search}
          onChange={e => { setSearch(e.target.value); table.setPage(0); }}
          className="max-w-sm"
        />
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          {...table}
          onPageChange={table.setPage}
          onPerPageChange={table.setPerPage}
          selectable
          selected={selected}
          onSelectAll={checked => setSelected(checked ? table.data.map(d => d.id) : [])}
          onSelectRow={(id, checked) => setSelected(prev => checked ? [...prev, id] : prev.filter(x => x !== id))}
          actions={row => (
            <div className="flex items-center gap-1">
              <button onClick={() => navigate(`/devices/${row.id}`)} className="btn-icon" title="View">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <button onClick={() => openEdit(row)} className="btn-icon" title="Edit">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </div>
          )}
        />
      </div>

      {/* Edit Modal */}
      <Modal open={!!editDevice} onClose={() => setEditDevice(null)} title="Update Device"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setEditDevice(null)}>Cancel</button>
            <button className="btn-primary" disabled={saving} onClick={handleSubmit(onSave)}>
              {saving ? <Spinner size="sm" /> : 'Save'}
            </button>
          </>
        }
      >
        <form className="space-y-4">
          <Input label="Device Name" error={errors.name?.message}
            {...register('name', { required: 'Name is required' })} />
          {/* Read-only: the serial is the device's identity and the key of its
              RTDB node (presence, actions, screenshots). Editing it here would
              orphan the player. It was an editable field that silently did
              nothing — the endpoint never accepted it. */}
          <div>
            <Input label="Serial Name" disabled readOnly
              value={editDevice?.serial_name ?? ''} />
            <p className="mt-1 text-xs text-text-muted">
              Identity — cannot be changed here.
            </p>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        open={!!deleteDevice}
        onClose={() => setDeleteDevice(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        message={`Delete device "${deleteDevice?.name ?? deleteDevice?.serial_name}"? This cannot be undone.`}
      />
    </Layout>
  );
}
